import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useProduct, useProducts } from "@/hooks/useData";
import { imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LeadDialog } from "@/components/LeadDialog";
import { ProductCard } from "@/components/ProductCard";
import { Eyebrow } from "@/components/Reveal";
import { ArrowLeft, Check } from "lucide-react";

const Spec = ({ label, value }) =>
  value ? (
    <div className="py-4 border-b border-[#ECEAE4] flex justify-between gap-6">
      <span className="label-eyebrow text-[10px] text-[#9E8773]">{label}</span>
      <span className="text-[#2C2A29] font-light text-right">{value}</span>
    </div>
  ) : null;

export default function ProductDetail() {
  const { id } = useParams();
  const { data: product, isLoading } = useProduct(id);
  const { data: all = [] } = useProducts();

  if (isLoading) return <div className="pt-44 text-center text-[#5E5A54]">Loading…</div>;
  if (!product) return <div className="pt-44 text-center text-[#5E5A54]" data-testid="product-not-found">Product not found.</div>;

  const related = all.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 4);

  return (
    <div className="pt-28 sm:pt-32" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/store" className="inline-flex items-center gap-2 label-eyebrow text-[11px] text-[#5E5A54] hover:text-[#2C2A29] transition-colors mb-8" data-testid="back-to-store">
          <ArrowLeft size={15} /> Back to store
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }} className="img-frame aspect-[4/5] bg-[#F6F5F2]">
            <img src={imgUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
          </motion.div>

          <div className="lg:py-6">
            <Eyebrow>{product.category}{product.collection ? ` · ${product.collection}` : ""}</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl font-medium text-[#2C2A29] mt-3" data-testid="product-name">{product.name}</h1>
            <p className="mt-5 text-lg text-[#5E5A54] font-light leading-relaxed">{product.description}</p>

            <div className="mt-8">
              <Spec label="Materials" value={product.materials} />
              <Spec label="Dimensions" value={product.dimensions} />
              <Spec label="Pricing" value={product.price_range} />
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <LeadDialog
                productName={product.name}
                trigger={
                  <Button className="rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-[#FFFFFF] h-12 px-8 label-eyebrow text-[10px]" data-testid="request-quote-button">
                    Request a Quote
                  </Button>
                }
              />
              <Button asChild variant="outline" className="rounded-none border-[#2C2A29] text-[#2C2A29] hover:bg-[#F6F5F2] h-12 px-8 label-eyebrow text-[10px]">
                <Link to="/contact" data-testid="product-contact-link">Talk to a Specialist</Link>
              </Button>
            </div>

            <ul className="mt-8 space-y-2">
              {["Customizable finishes & dimensions", "Factory-direct from vetted partners", "Pre-shipment inspection included"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-[#5E5A54] font-light">
                  <Check size={16} className="text-[#9E8773]" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {related.length > 0 && (
          <div className="py-20 sm:py-28">
            <h2 className="font-display text-3xl font-medium text-[#2C2A29] mb-10">You may also like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
